import type { Locale } from '@iac/contracts';
import type { ReactNode } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { DataTable, Td, Tr } from './DataTable';
import * as icons from './icons';

type Family = 'app' | 'nav' | 'small';

/**
 * Every `Icon*` export, read off the module, so a new icon shows up here
 * without editing this file. The prefix names the family and with it the size.
 */
function familyOf(name: string): Family {
  if (name.startsWith('IconApp')) return 'app';
  if (name.startsWith('IconNav')) return 'nav';
  return 'small';
}

const ICONS = Object.entries(icons)
  .filter(([name]) => name.startsWith('Icon'))
  .map(([name, Icon]) => ({
    name,
    // Every `Icon*` takes no props; the cast drops `AppIconFor` from the union.
    Icon: Icon as () => ReactNode,
    family: familyOf(name),
  }));

const sample: Dictionary<{ icon: string; name: string }> = {
  pl: { icon: 'Ikona', name: 'Eksport' },
  en: { icon: 'Icon', name: 'Export' },
};

function IconTable({ family, locale }: { family: Family; locale: Locale }) {
  const t = sample[locale];
  return (
    <DataTable
      columns={[{ label: t.icon, align: 'center' }, { label: t.name }]}
    >
      {ICONS.filter((icon) => icon.family === family).map(({ name, Icon }) => (
        <Tr key={name}>
          <Td>
            <Icon />
          </Td>
          <Td emphasis>
            <code>{name}</code>
          </Td>
        </Tr>
      ))}
    </DataTable>
  );
}

export default {
  kind: 'icons',
  title: { pl: 'Ikony', en: 'Icons' },
  description: {
    pl: 'Heroicons outline, 24 px, kreska 1,5 — nowe ikony trzymaj w tej rodzinie. Przedrostek nazwy wyznacza rozmiar: `IconApp*` na karty aplikacji (a `AppIconFor` wybiera je po kluczu), `IconNav*` do nawigacji, reszta to małe ikony przycisków.',
    en: 'Heroicons outline, 24px, stroke width 1.5 — keep new icons in that family. The name prefix sets the size: `IconApp*` for app cards (and `AppIconFor` picks one by key), `IconNav*` for navigation, the rest are small button icons.',
  },
  variants: [
    {
      name: { pl: 'Aplikacje', en: 'Apps' },
      render: (locale) => <IconTable family="app" locale={locale} />,
    },
    {
      name: { pl: 'Nawigacja', en: 'Navigation' },
      render: (locale) => <IconTable family="nav" locale={locale} />,
    },
    {
      name: { pl: 'Małe', en: 'Small' },
      render: (locale) => <IconTable family="small" locale={locale} />,
    },
  ],
} satisfies Story;
